import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaGlobe, FaMobile, FaCode, FaDesktop } from 'react-icons/fa';
import ScrollDown from './ScrollDown';
import { useLocalizedProjects } from '../hooks/useLocalizedProject';
import profileImg from '../assets/Screenshot from 2025-07-19 18-44-01.png';
import Eco from '../assets/ec.png';
import xml from '../assets/xml.webp';
import comme from '../assets/ecommerce-online-shopping-vector-illustration_677179-93.webp';
import back from '../assets/Gemini_Generated_Image_hfkfoghfkfoghfkf.png';
import wallet from '../assets/wallet.png';
import bmi from '../assets/bmi.png';
import wea from '../assets/wea.png';
import cr from '../assets/cr.png';
import ais from '../assets/ais.png';
import mara from '../assets/mara.png';
import scr from '../assets/scr.png';
import eventify from '../assets/eventify.png';
import applyflow from '../assets/applyflow.png';
import rentalManagement from '../assets/rental-management.png';
import rentalLogin from '../assets/rental-login.png';
import rentalDashboard from '../assets/rental-dashboard.png';
import rentalProducts from '../assets/rental-products.png';
import rentalRentals from '../assets/rental-rentals.png';
import rentalTenants from '../assets/rental-tenants.png';

const getCategoryMeta = (category, t) => {
  const meta = {
    web: { badgeClass: 'badge-web', Icon: FaGlobe, label: t('projects.badges.web') },
    mobile: { badgeClass: 'badge-mobile', Icon: FaMobile, label: t('projects.badges.mobile') },
    desktop: { badgeClass: 'badge-desktop', Icon: FaDesktop, label: t('projects.badges.desktop') },
  };
  return meta[category] || meta.web;
};

/** Optional per project: images[] (carousel), videos[] ({ url, title? }) — YouTube, Vimeo, or .mp4 */
export const projects = [
  {
    id: 'applyflow',
    title: 'ApplyFlow — Job Application Tracker + Chrome Autofill',
    description:
      'Full-stack job search assistant: Kanban application tracker, profile and multi-CV management, reusable Answer Vault snippets, and a Chrome Extension (Manifest V3) that autofills job forms from saved profile data. Built with Next.js, NestJS, MongoDB, and Stripe-ready freemium architecture.',
    technologies: [
      'Next.js',
      'NestJS',
      'TypeScript',
      'MongoDB',
      'Tailwind CSS',
      'Chrome Extension',
      'Stripe',
      'JWT',
    ],
    link: '#',
    image: applyflow,
    images: [applyflow],
    videos: [],
    category: 'web',
    features: [
      'JWT Authentication & Google OAuth',
      'Kanban Application Tracker (CRUD + Drag & Drop)',
      'Profile & Multi-CV PDF Management',
      'Answer Vault — Reusable Application Snippets',
      'Chrome Extension Autofill (Manifest V3)',
      'Save Applications Directly from Extension',
      'Admin Panel (Users, Applications, Feedback)',
      'Freemium Model with Stripe Integration',
      'Bilingual EN / FR (Web + Extension)',
    ],
    challenges:
      'Building reliable MV3 autofill across diverse job site form structures, securing JWT sync between the web app and Chrome extension, and designing a scalable freemium architecture with server-side premium enforcement',
    github: '#',
  },
  {
    id: 'eventify-platform',
    title: 'Eventify – Full-Stack Event Booking Platform',
    description:
      'Built a full-stack event booking platform allowing users to browse events, purchase tickets securely, receive email/SMS notifications, and manage their profiles. Includes a React admin dashboard for event management, user administration, and analytics, powered by a Node.js REST API with PostgreSQL.',
    technologies: [
      'Node.js',
      'Express.js',
      'PostgreSQL',
      'React.js',
      'Tailwind CSS',
      'Stripe',
      'JWT',
      'Twilio',
      'Nodemailer',
    ],
    link: '#',
    image: eventify,
    images: [eventify],
    videos: [],
    category: 'web',
    features: [
      'User Authentication & Authorization (JWT)',
      'Event Management System (CRUD)',
      'Stripe Payment Gateway Integration',
      'OTP Verification via Twilio SMS',
      'Email Notifications with Nodemailer',
      'Admin Analytics Dashboard',
      'User & Admin Role Management',
      'RESTful API Architecture',
      'PostgreSQL Database Design',
    ],
    challenges:
      'Designing a secure payment flow with Stripe, orchestrating OTP verification and email/SMS notifications, and building a scalable REST API shared by web admin and mobile clients',
    github: 'https://github.com/medaminenasfi/event-book',
  },
  {
    id: 'eventify-mobile',
    title: 'Eventify – Cross-Platform Mobile App',
    description:
      'Cross-platform Flutter mobile application connected to the Eventify REST API. Users can discover events, select tickets, proceed to secure Stripe checkout, and manage their bookings — sharing the same backend as the web admin dashboard.',
    technologies: ['Flutter', 'Dart', 'REST API', 'JWT', 'Stripe', 'Material Design'],
    link: '#',
    image: eventify,
    images: [eventify],
    videos: [],
    category: 'mobile',
    features: [
      'Browse & Discover Events',
      'Ticket Selection & Secure Checkout',
      'Stripe Payment Integration',
      'User Profile & Booking Management',
      'OTP Verification Flow',
      'Cross-Platform iOS & Android',
      'REST API Integration',
      'Modern Material Design UI',
    ],
    challenges:
      'Building a responsive Flutter UI synced with the REST API, handling secure payment flows on mobile, and maintaining consistent state across authentication and ticket purchase workflows',
    github: 'https://github.com/medaminenasfi/event-book',
  },
  {
    id: 'rental-management-system',
    title: 'Rental Management System – Desktop Application',
    description:
      'Offline-first Windows desktop application for managing bed and equipment rentals. Administrators manage products, tenants, monthly and yearly contracts, payments, financial reporting, and one-click SQLite backups — all through a French PyQt5 interface with TND currency support.',
    technologies: ['Python', 'PyQt5', 'SQLite', 'MVC Architecture', 'PyInstaller'],
    link: '#',
    image: rentalManagement,
    images: [
      rentalManagement,
      rentalLogin,
      rentalDashboard,
      rentalProducts,
      rentalRentals,
      rentalTenants,
    ],
    videos: [],
    category: 'desktop',
    features: [
      'Secure Admin Authentication & Session Management',
      'Product Management (Beds & Equipment)',
      'Monthly & Yearly Rental Contracts',
      'Tenant Management with Search & Filtering',
      'Financial Dashboard & Real-Time Statistics',
      'Deposit, Discount & Balance Calculations',
      'Payment Status Tracking (Paid / Unpaid)',
      'One-Click Database Backup & Recovery',
      'Offline-First SQLite Architecture',
      'PyInstaller Windows Executable Distribution',
    ],
    challenges:
      'Implementing accurate financial logic for monthly and yearly billing periods, designing an offline-first SQLite architecture with timestamped backups, and building a multi-module PyQt5 desktop UI with auto-refreshing dashboard statistics',
    github: 'https://github.com/medaminenasfi/desktop-rental-management-system',
  },
  {
    id: 'real-estate-saas',
    title: 'Club Rendement+ – Real Estate Investment Platform',
    description:
      'A scalable real estate investment platform connecting investors with high-return rental opportunities in Tunisia. Features investor onboarding, dynamic forms, lead management, and an admin dashboard.',
    technologies: ['Next.js', 'React', 'Node.js', 'PostgreSQL', 'Tailwind CSS'],
    link: '#',
    image: cr,
    images: [cr, mara],
    videos: [],
    category: 'web',
    features: [
      'Investor Onboarding Flow',
      'Admin Dashboard',
      'Lead Management System',
      'Dynamic Forms & Data Collection',
      'Responsive UI/UX',
      'Scalable SaaS Architecture',
      'CRM-Ready Structure',
    ],
    challenges: 'Designing a scalable architecture and building flexible lead management workflows',
    github: '#',
  },
  {
    id: 'medical-platform',
    title: 'AIS – Advanced Implant Services Medical Platform',
    description:
      'Official website for AIS, exclusive OSSTEM Implant representative in Tunisia. Full-stack medical implant platform with product catalog, quote request system, and admin-ready architecture.',
    technologies: ['Next.js', 'React', 'Node.js', 'PostgreSQL', 'Tailwind CSS'],
    link: '#',
    image: ais,
    category: 'web',
    features: [
      'Dynamic Product Catalog',
      'Quote Request System',
      'Technical Product Pages',
      'Responsive Design',
      'Structured Navigation',
      'Modern UI/UX',
      'Admin-Ready Architecture',
    ],
    challenges: 'Building scalable product management and creating an intuitive quote request workflow',
    github: '#',
  },
  {
    id: 'real-estate-landing',
    title: 'Real Estate SaaS Landing Page',
    description:
      'A modern SaaS landing page focused on conversion, clean UI/UX, reusable components, and responsive design for a real estate management platform.',
    technologies: ['Next.js', 'React', 'Tailwind CSS', 'Express.js', 'PostgreSQL'],
    link: '#',
    image: mara,
    category: 'web',
    features: [
      'Modern SaaS UI',
      'Responsive Design',
      'Reusable Components',
      'Smooth Animations',
      'Optimized Conversion Flow',
      'CTA-Focused Layout',
      'Clean User Experience',
    ],
    challenges: 'Creating a high-converting layout while maintaining performance and scalability',
    github: '#',
  },
  {
    id: 'universal-web-scraper',
    title: 'Universal Web Scraper – Automated Data Extraction',
    description:
      'A powerful automated web scraping system capable of extracting structured data from static and dynamic websites using only a URL.',
    technologies: ['Python', 'Requests', 'BeautifulSoup', 'Selenium', 'JSON'],
    link: '#',
    image: scr,
    category: 'web',
    features: [
      'Static & Dynamic Website Support',
      'Automated Data Extraction',
      'Image & Media Collection',
      'Table & Link Extraction',
      'Document Download Detection',
      'Structured JSON Output',
      'Scalable Scraping Workflow',
    ],
    challenges:
      'Handling dynamic websites and building a universal extraction logic adaptable to multiple site structures',
    github: '#',
  },
  {
    id: 'backend-ecommerce',
    title: 'Backend – E-commerce API',
    description:
      'A complete e-commerce backend API built with TypeScript, featuring user authentication, product management, order processing, and payment integration.',
    technologies: ['Node.js', 'TypeScript', 'MongoDB', 'Express.js', 'JWT', 'Stripe API', 'Bcrypt'],
    link: 'https://github.com/medaminenasfi/E-commerce/tree/main/backend',
    image: back,
    category: 'web',
    features: [
      'User Authentication & Authorization',
      'Product CRUD Operations',
      'Shopping Cart Management',
      'Order Processing System',
      'Payment Integration with Stripe',
      'Admin Dashboard',
      'RESTful API Design',
    ],
    challenges: 'Implementing secure payment processing and scalable database design',
    github: 'https://github.com/medaminenasfi/E-commerce/tree/main/backend',
  },
  {
    id: 'ecotourisme',
    title: 'Ecotourisme TN',
    description:
      'A comprehensive digital platform promoting eco-tourism and sustainable travel experiences in Tunisia with booking system and interactive maps.',
    technologies: ['React.js', 'Node.js', 'Express.js', 'MongoDB', 'Leaflet', 'Socket.io'],
    link: 'https://ecotourisme.laghazala.tn/',
    image: Eco,
    images: [Eco],
    videos: [],
    category: 'web',
    features: [
      'Interactive Tourism Maps',
      'Booking System',
      'Real-time Chat Support',
      'Multi-language Support',
      'Mobile Responsive Design',
      'Payment Gateway Integration',
      'Review & Rating System',
    ],
    challenges: 'Integrating real-time features and optimizing for mobile performance',
    github: 'https://github.com/medaminenasfi/ecotourisme',
  },
  {
    id: 'xml-generator',
    title: 'XML Generator',
    description:
      'Advanced application for converting Excel files and SQL queries to XML format with custom schema validation and batch processing.',
    technologies: ['React.js', 'Node.js', 'Excel API', 'XML Parser', 'Multer', 'Joi'],
    link: 'https://github.com/medaminenasfi/xml-generator',
    image: xml,
    category: 'web',
    features: [
      'Excel to XML Conversion',
      'SQL Query to XML Export',
      'Custom Schema Validation',
      'Batch File Processing',
      'Download Management',
      'Error Handling & Logging',
      'Preview Before Export',
    ],
    challenges: 'Handling large file uploads and complex XML schema validation',
    github: 'https://github.com/medaminenasfi/xml-generator',
  },
  {
    id: 'glooms-ecommerce',
    title: 'Glooms E-Commerce',
    description:
      'Modern full-stack e-commerce platform specializing in clothing for all age groups with advanced filtering and recommendation system.',
    technologies: ['React.js', 'Node.js', 'MongoDB', 'Redux', 'Tailwind CSS', 'Cloudinary'],
    link: 'https://github.com/medaminenasfi/glooms-ecommerce',
    image: comme,
    category: 'web',
    features: [
      'Product Catalog with Filters',
      'User Profiles & Wishlists',
      'Shopping Cart & Checkout',
      'Order Tracking',
      'Admin Panel',
      'Image Upload & Management',
      'Responsive Design',
    ],
    challenges: 'Implementing complex product filtering and optimizing image loading',
    github: 'https://github.com/medaminenasfi/glooms-ecommerce',
  },
  {
    id: 'traducode',
    title: 'TraduCode',
    description:
      'Innovative web application that converts algorithmic pseudocode into actual programming code across multiple languages.',
    technologies: ['HTML', 'CSS', 'JavaScript', 'Prism.js', 'Monaco Editor', 'Regex'],
    link: 'https://github.com/medaminenasfi/traducode',
    image: profileImg,
    category: 'web',
    features: [
      'Multi-language Code Generation',
      'Syntax Highlighting',
      'Code Editor Integration',
      'Algorithm Visualization',
      'Export Functionality',
      'Code Validation',
      'Interactive Examples',
    ],
    challenges: 'Parsing complex algorithms and generating syntactically correct code',
    github: 'https://github.com/medaminenasfi/traducode',
  },
  {
    id: 'bmi-calculator',
    title: 'BMI Calculator',
    description:
      'A simple and elegant Body Mass Index (BMI) Calculator built using Flutter and Dart.',
    technologies: ['Flutter', 'Dart', 'Material Design'],
    link: 'https://github.com/medaminenasfi/BMI-Calculator',
    image: bmi,
    category: 'mobile',
    features: [
      'Easy BMI Calculation',
      'Supports Metric Units (kg, cm)',
      'Displays Health Categories',
      'Clean and Responsive UI',
    ],
    challenges: 'Ensuring accuracy in BMI calculation and maintaining a clean responsive layout',
    github: 'https://github.com/medaminenasfi/BMI-Calculator',
  },
  {
    id: 'portefeuille-flutter',
    title: 'Digital Wallet Flutter App',
    description:
      'Flutter app with currency converter and monthly budget simulator for simple personal finance management.',
    technologies: ['Flutter', 'Dart', 'Material Design'],
    link: 'https://github.com/medaminenasfi/portefeuille-numerique',
    image: wallet,
    category: 'mobile',
    features: [
      'Currency converter with fixed rates',
      'Monthly budget simulator',
      'Display remaining balance after expenses',
      'Simple and smooth navigation',
    ],
    challenges: 'Designing a smooth and intuitive interface with reliable calculations',
    github: 'https://github.com/medaminenasfi/portefeuille-numerique',
  },
  {
    id: 'weather-app',
    title: 'Weather Forecast App (Ongoing)',
    description:
      'An elegant Flutter application providing real-time weather updates and detailed forecasts with UI animations.',
    technologies: ['Flutter', 'Dart', 'REST API', 'Geolocator', 'Weather API', 'Animations'],
    link: 'https://github.com/medaminenasfi/flutter-weather',
    image: wea,
    category: 'mobile',
    features: [
      'Real-time Weather Data (in progress)',
      '7-Day Forecast (in progress)',
      'Location-based Weather Detection',
      'Smooth UI Animations',
    ],
    status: 'In Development',
    challenges:
      'Integrating live APIs and optimizing animation performance while managing location permissions.',
    github: 'https://github.com/medaminenasfi/flutter-weather',
  },
];

const ProjectCard = ({ project, onClick, t }) => {
  const { badgeClass, Icon, label } = getCategoryMeta(project.category, t);

  return (
  <motion.div
    className="group relative rounded-2xl overflow-hidden bg-gradient-to-br from-primary/90 to-primary/70 shadow-xl hover:shadow-2xl transition-all duration-500 border border-secondary/20 hover:border-secondary/50 transform flex flex-col cursor-pointer w-full h-full min-h-[460px] xs:min-h-[500px] sm:min-h-[540px]"
    onClick={() => onClick(project)}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{
      y: -8,
      scale: 1.02,
    }}
    transition={{ duration: 0.3, type: 'spring', stiffness: 300 }}
  >
    <motion.div
      className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-10"
    />

    <div className="w-full h-48 sm:h-52 md:h-56 shrink-0 overflow-hidden rounded-t-2xl relative">
      <motion.img
        src={project.image}
        alt={project.title}
        className="w-full h-full object-cover"
        whileHover={{ scale: 1.1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>

    <motion.div
      className="absolute top-3 right-3 z-20"
      whileHover={{ scale: 1.1, rotate: 5 }}
      transition={{ type: 'spring', stiffness: 400 }}
    >
      <span
        className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-2 shadow-lg backdrop-blur-sm ${badgeClass}`}
      >
        <Icon className="w-3 h-3" />
        {label}
      </span>
    </motion.div>

    <div className="flex flex-col flex-1 p-4 sm:p-5 md:p-6 relative z-10 min-h-0">
      <motion.h3
        className="text-base sm:text-lg md:text-xl font-bold text-white mb-2 min-h-[3.25rem] sm:min-h-[3.5rem] line-clamp-2 group-hover:text-secondary transition-colors duration-300"
        whileHover={{ x: 4 }}
      >
        {project.title}
      </motion.h3>
      <p className="text-textPrimary mb-3 sm:mb-4 min-h-[4.5rem] sm:min-h-[5rem] line-clamp-3 text-xs sm:text-sm leading-relaxed flex-1">
        {project.description}
      </p>
      <div className="flex flex-wrap gap-2 mb-4 min-h-[2rem] content-start">
        {project.technologies.slice(0, 3).map((tech) => (
          <motion.span
            key={tech}
            className="px-2 sm:px-3 py-1 sm:py-1.5 bg-secondary/20 text-secondary rounded-full text-xs font-medium border border-secondary/30"
            whileHover={{ scale: 1.1 }}
            transition={{ type: 'spring', stiffness: 400 }}
          >
            {tech}
          </motion.span>
        ))}
        {project.technologies.length > 3 && (
          <span className="px-2 sm:px-3 py-1 sm:py-1.5 bg-secondary/10 text-secondary/70 rounded-full text-xs font-medium">
            {t('projects.moreTechnologies', { count: project.technologies.length - 3 })}
          </span>
        )}
      </div>
      <div
        className="project-detail-btn mt-auto w-full min-h-[48px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm sm:text-base border-2 border-secondary bg-primary/90 text-secondary shadow-[0_0_14px_rgba(100,255,218,0.2)] group-hover:bg-secondary group-hover:text-primary group-hover:shadow-[0_0_22px_rgba(100,255,218,0.4)] transition-all duration-300"
        aria-hidden="true"
      >
        <span className="whitespace-nowrap">{t('projects.viewDetails')}</span>
        <svg
          className="w-4 h-4 shrink-0 group-hover:translate-x-1 transition-transform duration-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </div>
    </div>
  </motion.div>
  );
};

const Projects = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localizedProjects = useLocalizedProjects(projects);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(1);

  const categories = [
    { id: 'all', label: t('projects.categories.all'), icon: <FaCode className="w-5 h-5 sm:w-4 sm:h-4" /> },
    { id: 'web', label: t('projects.categories.web'), icon: <FaGlobe className="w-5 h-5 sm:w-4 sm:h-4" /> },
    { id: 'mobile', label: t('projects.categories.mobile'), icon: <FaMobile className="w-5 h-5 sm:w-4 sm:h-4" /> },
    { id: 'desktop', label: t('projects.categories.desktop'), icon: <FaDesktop className="w-5 h-5 sm:w-4 sm:h-4" /> },
  ];

  const filteredProjects =
    activeCategory === 'all'
      ? localizedProjects
      : localizedProjects.filter((project) => project.category === activeCategory);

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth >= 1024) setItemsPerPage(3);
      else if (window.innerWidth >= 768) setItemsPerPage(2);
      else setItemsPerPage(1);
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / itemsPerPage));

  useEffect(() => {
    setCurrentIndex(0);
  }, [activeCategory, itemsPerPage]);

  useEffect(() => {
    if (currentIndex >= totalPages) {
      setCurrentIndex(0);
    }
  }, [currentIndex, totalPages]);

  const touchStartX = useRef(null);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % totalPages);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null || totalPages <= 1) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    touchStartX.current = null;
    if (Math.abs(diff) < 50) return;
    if (diff > 0) nextSlide();
    else prevSlide();
  };

  const getCurrentProjects = () => {
    const start = currentIndex * itemsPerPage;
    return filteredProjects.slice(start, start + itemsPerPage);
  };

  const handleProjectClick = (project) => {
    navigate(`/project/${project.id}`, { state: { project } });
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentIndex(0);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="projects" className="section-padding relative overflow-x-clip">
      <motion.div
        className="container mx-auto"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.h2 className="heading" variants={itemVariants}>
          {t('projects.title')}
        </motion.h2>

        <motion.div
          className="flex flex-nowrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-12"
          variants={itemVariants}
        >
          {categories.map((category) => {
            const count =
              category.id === 'all'
                ? localizedProjects.length
                : localizedProjects.filter((p) => p.category === category.id).length;

            return (
              <button
                key={category.id}
                type="button"
                onClick={() => handleCategoryChange(category.id)}
                aria-label={`${category.label} (${count})`}
                aria-pressed={activeCategory === category.id}
                title={category.label}
                className={`relative flex items-center justify-center gap-2 p-3 sm:px-6 sm:py-3 rounded-full font-semibold transition-all duration-300 text-sm sm:text-base ${
                  activeCategory === category.id
                    ? 'bg-secondary text-primary shadow-lg shadow-secondary/25 scale-105'
                    : 'bg-primary/50 text-textSecondary hover:bg-secondary/20 hover:text-secondary'
                }`}
              >
                {category.icon}
                <span className="hidden sm:inline">{category.label}</span>
                <span
                  className={`absolute -top-1 -right-1 sm:static sm:ml-1 flex h-5 min-w-[1.25rem] items-center justify-center px-1 rounded-full text-[10px] sm:text-xs font-bold ${
                    activeCategory === category.id ? 'bg-primary/20 text-primary' : 'bg-secondary/20 text-secondary'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </motion.div>

        <div className="relative px-1 sm:px-4 lg:px-6">
          <div className={totalPages > 1 ? 'md:flex md:items-center md:gap-3 lg:gap-4' : ''}>
            {totalPages > 1 && (
              <button
                type="button"
                onClick={prevSlide}
                aria-label={t('projects.carousel.previousProjects')}
                className="project-carousel-nav hidden md:flex shrink-0 bg-secondary/20 hover:bg-secondary/40 text-white p-3 rounded-full transition-colors duration-300 items-center justify-center"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            <div
              className="flex-1 min-w-0 touch-pan-y"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${activeCategory}-${currentIndex}`}
                  className="grid gap-6 sm:gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-stretch"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                >
                  {getCurrentProjects().map((project) => (
                    <motion.div key={project.id} variants={itemVariants} className="h-full flex">
                      <ProjectCard project={project} onClick={handleProjectClick} t={t} />
                    </motion.div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            {totalPages > 1 && (
              <button
                type="button"
                onClick={nextSlide}
                aria-label={t('projects.carousel.nextProjects')}
                className="project-carousel-nav hidden md:flex shrink-0 bg-secondary/20 hover:bg-secondary/40 text-white p-3 rounded-full transition-colors duration-300 items-center justify-center"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>

          {totalPages > 1 && (
            <>
              <div className="flex md:hidden items-center justify-between gap-3 mt-5 px-1">
                <button
                  type="button"
                  onClick={prevSlide}
                  aria-label={t('projects.carousel.previousProject')}
                  className="project-carousel-nav flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-secondary/20 hover:bg-secondary/40 text-white text-sm font-medium transition-colors duration-300"
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  {t('projects.carousel.prev')}
                </button>

                <span className="text-sm text-textSecondary tabular-nums shrink-0">
                  {t('projects.carousel.pageOf', { current: currentIndex + 1, total: totalPages })}
                </span>

                <button
                  type="button"
                  onClick={nextSlide}
                  aria-label={t('projects.carousel.nextProject')}
                  className="project-carousel-nav flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-secondary/20 hover:bg-secondary/40 text-white text-sm font-medium transition-colors duration-300"
                >
                  {t('projects.carousel.next')}
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="hidden sm:flex justify-center mt-6 gap-2 flex-wrap px-2">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    aria-label={t('projects.carousel.goToPage', { page: index + 1 })}
                    aria-current={index === currentIndex ? 'true' : undefined}
                    onClick={() => setCurrentIndex(index)}
                    className={`project-carousel-dot rounded-full transition-colors duration-300 ${
                      index === currentIndex ? 'bg-secondary' : 'bg-secondary/30'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {filteredProjects.length === 0 && (
          <motion.div className="text-center py-20" variants={itemVariants}>
            <div className="text-6xl mb-4">🚧</div>
            <h3 className="text-xl font-semibold text-textSecondary mb-2">{t('projects.empty.comingSoon')}</h3>
            <p className="text-textSecondary">
              {activeCategory === 'mobile'
                ? t('projects.empty.mobileInDevelopment')
                : t('projects.empty.noProjectsFound')}
            </p>
          </motion.div>
        )}

        <div className="absolute top-1/2 right-0 -z-10">
          <motion.div
            className="blur-decoration w-64 h-64 sm:w-96 sm:h-96 bg-secondary/5 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          />
        </div>
      </motion.div>

      <ScrollDown targetId="skills" inline />
    </section>
  );
};

export default Projects;
